import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Alert, LoadingState } from '@dreamtree-org/twreact-ui';
import { errorText } from '@/lib/format';
import { useQuery, useMutation } from '@/lib/useQuery';
import { VendorAuthFrame } from './SignIn';
import { useVendorSession } from '../VendorApp';
import { PASSWORD_HINT } from '../lib/password';

/**
 * Set a password from an emailed link (MERCH-107): the invite (7 days) or a
 * reset (1 hour), `/vendor/set-password#token=…`. The token is read once (the
 * fragment, or the older `?token=` form), kept in state, and stripped from the
 * address bar and history (security review L1). The link is checked first, so an
 * expired one says so before anything is typed. Setting the password signs out
 * every session; the vendor then signs in with the new password.
 */

/** The token from `#token=` or `?token=`; '' when neither. */
export function tokenFromLocation(loc = window.location) {
  const fromHash = new URLSearchParams(String(loc.hash || '').replace(/^#/, '')).get('token');
  return fromHash || new URLSearchParams(loc.search || '').get('token') || '';
}
export default function SetPassword({ store }) {
  const [token] = useState(() => tokenFromLocation());
  useEffect(() => {
    const { pathname, search, hash } = window.location;
    if (search || hash) window.history.replaceState(window.history.state, '', pathname);
  }, []);
  const link = useQuery('vendorPasswordLink', { token }, { select: (r) => r.data, enabled: !!token });
  const setPassword = useMutation('vendorSetPassword');
  const { vendor, reload } = useVendorSession();
  const [form, setForm] = useState({ password: '', confirm: '' });
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirm) return setError('The two passwords do not match.');
    try {
      await setPassword.run({ token, password: form.password });
      setDone(true);
      if (vendor) reload(); // this browser's session ended with the others
    } catch (ex) {
      setError(errorText(ex));
    }
  };

  let body;
  if (done) {
    body = (
      <div className="space-y-3">
        <Alert variant="success" title="Password set">Sign in with your new password. Any other device you were signed in on is signed out.</Alert>
        <Button as={Link} to="/login" className="w-full">Sign in</Button>
      </div>
    );
  } else if (token && link.loading) {
    body = <LoadingState label="Checking your link…" />;
  } else if (!token || link.error || !link.data) {
    body = (
      <div className="space-y-3">
        <Alert variant="error" title="Link expired">{link.error || 'This link is not valid any more. Ask for a new one.'}</Alert>
        <Button as={Link} to="/forgot-password" className="w-full">Email me a new link</Button>
      </div>
    );
  } else {
    body = (
      <form className="space-y-3" onSubmit={submit}>
        <p className="text-sm opacity-80">For <span className="font-medium">{link.data.business_name}</span> ({link.data.email})</p>
        <Input label="New password" type="password" autoComplete="new-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} helperText={PASSWORD_HINT} required />
        <Input label="Repeat the password" type="password" autoComplete="new-password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
        {error ? <Alert variant="error" title="Password not set">{error}</Alert> : null}
        <Button type="submit" className="w-full" loading={setPassword.loading}>Set password</Button>
      </form>
    );
  }

  return (
    <VendorAuthFrame store={store} title="Set your password" subtitle={`Vendor account at ${(store && store.name) || 'this store'}.`}>
      {body}
      {done ? null : <p className="text-sm text-center opacity-80"><Link className="font-medium underline" to="/login">Back to sign in</Link></p>}
    </VendorAuthFrame>
  );
}
